import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Box, CssBaseline, Toolbar, Typography, Tabs, Tab, Avatar, Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Description as TemplatesIcon,
  Campaign as BroadcastIcon,
  PhoneAndroid as CredentialsIcon,
} from '@mui/icons-material';
import AccountsView from './views/AccountsView';
import OrganizationsView from './views/OrganizationsView';
import TemplatesAdminView from './views/TemplatesAdminView';
import BroadcastsAdminView from './views/BroadcastsAdminView';
import CredentialsView from './views/CredentialsView';

const NAV_TABS = [
  { id: 'accounts', label: 'Accounts', icon: <PeopleIcon fontSize="small" />, path: '/admin/accounts' },
  { id: 'organizations', label: 'Organizations', icon: <BusinessIcon fontSize="small" />, path: '/admin/organizations' },
  { id: 'templates', label: 'Templates', icon: <TemplatesIcon fontSize="small" />, path: '/admin/templates' },
  { id: 'broadcasts', label: 'Broadcasts', icon: <BroadcastIcon fontSize="small" />, path: '/admin/broadcasts' },
  { id: 'credentials', label: 'Credentials', icon: <CredentialsIcon fontSize="small" />, path: '/admin/credentials' },
];

function getActiveTab(pathname) {
  if (pathname.startsWith('/admin/organizations')) return '/admin/organizations';
  if (pathname.startsWith('/admin/templates')) return '/admin/templates';
  if (pathname.startsWith('/admin/broadcasts')) return '/admin/broadcasts';
  if (pathname.startsWith('/admin/credentials')) return '/admin/credentials';
  return '/admin/accounts';
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = getActiveTab(location.pathname);

  function handleSignOut() {
    const url = `${process.env.REACT_APP_API_URL}/signout`;
    fetch(url, { method: 'POST', credentials: 'include' }).finally(() => {
      window.location.replace('/signin');
    });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: '#1a237e', color: '#fff' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56 }}>
          {/* Logo + title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: '#fff', color: '#1a237e', fontSize: 14, fontWeight: 700 }}>
              A
            </Avatar>
            <Typography variant="h6" fontWeight={700} fontSize={16}>
              WappGrow Admin
            </Typography>
          </Box>

          {/* Nav tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => navigate(v)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ '& .MuiTab-root': { minHeight: 56, fontSize: 13, textTransform: 'none', gap: 0.5 } }}
          >
            {NAV_TABS.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.path}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          {/* Sign out */}
          <Button color="inherit" size="small" onClick={handleSignOut} sx={{ fontSize: 13 }}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: '56px', flex: 1, overflow: 'auto', bgcolor: '#f5f5f5' }}>
        <Routes>
          <Route path="accounts" element={<AccountsView />} />
          <Route path="organizations" element={<OrganizationsView />} />
          <Route path="templates" element={<TemplatesAdminView />} />
          <Route path="broadcasts" element={<BroadcastsAdminView />} />
          <Route path="credentials" element={<CredentialsView />} />
          <Route path="*" element={<Navigate to="accounts" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}
